import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AnalystContext = createContext(null);
export const useAnalyst = () => useContext(AnalystContext);

export const AnalystProvider = ({ children }) => {
  const { user } = useAuth();
  const [context, setContext] = useState(null);        // currently-viewed listing {item_type,item_id,title}
  const [pins, setPins] = useState(() => {              // {A, B} listings pinned for A/B comparison
    try { return JSON.parse(localStorage.getItem("vaelith_pins") || "{}") || {}; } catch { return {}; }
  });
  const [open, setOpen] = useState(false);             // floating panel open state
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);        // [{role,text,kind,results,negotiation}]
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const loadedFor = useRef(null);

  const refreshList = useCallback(async () => {
    try {
      const r = await api.get("/ai/conversations");
      setConversations(r.data.conversations || []);
      return r.data.conversations || [];
    } catch {
      return [];
    }
  }, []);

  const selectConversation = useCallback(async (id) => {
    if (!id) return;
    setActiveId(id);
    setLoadingConv(true);
    try {
      const r = await api.get(`/ai/conversations/${id}`);
      setMessages(r.data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setLoadingConv(false);
    }
  }, []);

  const newChat = useCallback(() => {
    setActiveId(null);
    setMessages([]);
  }, []);

  // Load the user's saved conversations once signed in; resume the most recent.
  useEffect(() => {
    if (!user) {
      setConversations([]); setMessages([]); setActiveId(null); loadedFor.current = null;
      return;
    }
    if (loadedFor.current === user.user_id) return;
    loadedFor.current = user.user_id;
    (async () => {
      const list = await refreshList();
      if (list.length) selectConversation(list[0].id);
    })();
  }, [user, refreshList, selectConversation]);

  const sendMessage = useCallback(async (text, listingContext, opts = {}) => {
    const q = (text || "").trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const r = await api.post("/ai/chat", {
        message: q,
        conversation_id: activeId || undefined,
        context: listingContext || (context?.item_id ? context : undefined),
        compare: opts.compare || undefined,
      });
      setActiveId(r.data.conversation_id);
      setMessages((m) => [...m, {
        role: "assistant", text: r.data.reply, kind: r.data.kind,
        results: r.data.results, negotiation: r.data.negotiation,
        search: r.data.search, comparison: r.data.comparison,
      }]);
      refreshList();
    } catch (e) {
      const status = e?.response?.status;
      setMessages((m) => [...m, {
        role: "assistant",
        text: status === 401 ? "Please sign in to use Vaelith AI." : "Vaelith AI is unavailable right now. Please try again.",
        kind: "text",
      }]);
    } finally {
      setSending(false);
    }
  }, [activeId, context, sending, refreshList]);

  const deleteConversation = useCallback(async (id) => {
    try { await api.delete(`/ai/conversations/${id}`); } catch { /* ignore */ }
    const list = await refreshList();
    if (id === activeId) {
      if (list.length) selectConversation(list[0].id);
      else newChat();
    }
  }, [activeId, refreshList, selectConversation, newChat]);

  const renameConversation = useCallback(async (id, title) => {
    try { await api.patch(`/ai/conversations/${id}`, { title }); } catch { /* ignore */ }
    refreshList();
  }, [refreshList]);

  const pinConversation = useCallback(async (id, pinned) => {
    try { await api.patch(`/ai/conversations/${id}`, { pinned }); } catch { /* ignore */ }
    refreshList();
  }, [refreshList]);

  const saveAlert = useCallback(async (search) => {
    if (!search) return null;
    try {
      const r = await api.post("/ai/alerts", search);
      return r.data;
    } catch {
      return null;
    }
  }, []);

  // Backwards-compatible entry point used across pages: open + optionally ask.
  const ask = useCallback((query, listingContext) => {
    setOpen(true);
    if (query) sendMessage(query, listingContext);
  }, [sendMessage]);

  // Pin a listing into slot A or B (from a detail page or a chat result) for an A/B comparison.
  const pinToSlot = useCallback((slot, item) => {
    if (!item?.item_id || (slot !== "A" && slot !== "B")) return;
    setPins((p) => {
      const next = { ...p, [slot]: item };
      try { localStorage.setItem("vaelith_pins", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setOpen(true);
  }, []);
  const clearPin = useCallback((slot) => {
    setPins((p) => {
      const next = { ...p }; delete next[slot];
      try { localStorage.setItem("vaelith_pins", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const clearPins = useCallback(() => {
    setPins({});
    try { localStorage.removeItem("vaelith_pins"); } catch { /* ignore */ }
  }, []);

  return (
    <AnalystContext.Provider
      value={{
        context, setContext,
        pins, pinToSlot, clearPin, clearPins,
        open, setOpen,
        conversations, activeId, messages, sending, loadingConv,
        sendMessage, newChat, selectConversation, deleteConversation, refreshList,
        renameConversation, pinConversation, saveAlert,
        ask,
      }}
    >
      {children}
    </AnalystContext.Provider>
  );
};
